import {useMemo, useState} from 'react'
import {Alert, Spinner} from 'react-bootstrap'
import {useMutation, useQuery} from '@apollo/client'
import {toast} from 'react-toastify'

import {GET_MESSAGE_TEMPLATES} from '../../gql/queries/campaigns'
import {CREATE_MESSAGE_TEMPLATE, UPDATE_MESSAGE_TEMPLATE} from '../../gql/mutations/campaigns'
import {IMessageTemplate, IMessageTemplatesRes, CampaignChannel} from '../../types/campaigns'
import {TemplatesPanel} from '../campaigns/components/TemplatesPanel'
import {TemplateFormModal} from '../campaigns/components/TemplateFormModal'

export const TemplatesPage = () => {
  const [modalOpen, setModalOpen] = useState(false)
  const [editingTemplate, setEditingTemplate] = useState<IMessageTemplate | null>(null)
  const [defaultChannel, setDefaultChannel] = useState<CampaignChannel | undefined>(undefined)

  const {
    data,
    loading,
    error,
    refetch,
  } = useQuery<IMessageTemplatesRes>(GET_MESSAGE_TEMPLATES, {
    fetchPolicy: 'cache-and-network',
  })

  const templates = useMemo(() => data?.messageTemplates ?? [], [data])

  const [createTemplateMutation, {loading: creating}] = useMutation(CREATE_MESSAGE_TEMPLATE)
  const [updateTemplateMutation, {loading: updating}] = useMutation(UPDATE_MESSAGE_TEMPLATE)

  const handleClose = () => {
    setModalOpen(false)
    setEditingTemplate(null)
    setDefaultChannel(undefined)
  }

  const handleOpenCreate = (channel?: CampaignChannel) => {
    setEditingTemplate(null)
    setDefaultChannel(channel)
    setModalOpen(true)
  }

  const handleEditTemplate = (template: IMessageTemplate) => {
    setEditingTemplate(template)
    setDefaultChannel(template.channel)
    setModalOpen(true)
  }

  const handleSubmit = async (payload: {
    name: string
    description?: string
    channel: CampaignChannel
    subject?: string
    body: string
    variables?: string[]
    metadata?: Record<string, any>
  }) => {
    try {
      if (editingTemplate) {
        await updateTemplateMutation({
          variables: {
            id: editingTemplate.id,
            ...payload,
          },
        })
        toast.success('Template updated')
      } else {
        await createTemplateMutation({
          variables: payload,
        })
        toast.success('Template created')
      }
      handleClose()
      await refetch()
    } catch (error) {
      toast.error('Unable to save template')
    }
  }

  return (
    <div className='templates-page'>
      <div className='d-flex align-items-center justify-content-between mb-6 flex-wrap gap-3'>
        <div>
          <h1 className='page-title'>Messaging templates</h1>
          <div className='text-muted'>
            Centralize SMS, email, WhatsApp and Telegram content. 
            Create multi-channel variants for each template.
          </div>
        </div>
        {loading && <Spinner animation='border' size='sm' />}
      </div>
      {error && (
        <Alert variant='danger'>
          Unable to load templates. {error.message}
        </Alert>
      )}
      <TemplatesPanel templates={templates} onCreate={() => handleOpenCreate()} onEdit={handleEditTemplate} />
      <TemplateFormModal
        show={modalOpen}
        onHide={handleClose}
        onSubmit={handleSubmit}
        template={editingTemplate}
        loading={creating || updating}
        defaultChannel={defaultChannel}
      />
    </div>
  )
}
